import { Server } from 'lincd-server-utils/utils/Server';
import { useEffect, useState } from 'react';
import { packageName } from '../package.js';

export interface AppConfigState {
  showTorchBanner: boolean;
  torchesLitCount: number;
  peaceFlameRegisterTeamId: string;
  loading: boolean;
}

interface AppConfigPayload {
  showTorchBanner: boolean;
  torchesLitCount: number;
  peaceFlameRegisterTeamId: string;
}

const REVALIDATE_MS = 60_000;

let cache: AppConfigPayload | null = null;
let lastFetchAt = 0;
let inFlight: Promise<AppConfigPayload> | null = null;
const subscribers = new Set<() => void>();

const DEFAULT_PAYLOAD: AppConfigPayload = {
  showTorchBanner: true,
  torchesLitCount: 0,
  peaceFlameRegisterTeamId: '',
};

async function fetchAppConfig(): Promise<AppConfigPayload> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const result = await Server.call(packageName, 'getAppConfig');
      if (
        result &&
        typeof result.showTorchBanner === 'boolean' &&
        typeof result.torchesLitCount === 'number'
      ) {
        cache = {
          showTorchBanner: result.showTorchBanner,
          torchesLitCount: result.torchesLitCount,
          peaceFlameRegisterTeamId:
            typeof result.peaceFlameRegisterTeamId === 'string'
              ? result.peaceFlameRegisterTeamId
              : '',
        };
        lastFetchAt = Date.now();
      }
      return cache ?? DEFAULT_PAYLOAD;
    } catch (err) {
      console.error('useAppConfig: getAppConfig failed', err);
      return cache ?? DEFAULT_PAYLOAD;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Manually invalidate the cached app config (e.g. after an admin save).
 * Triggers all mounted useAppConfig hooks to refetch on next tick.
 */
export function invalidateAppConfigCache(): void {
  cache = null;
  lastFetchAt = 0;
  for (const cb of subscribers) cb();
}

/**
 * Hook for reading the global app config (singleton).
 *
 * Returns optimistic defaults
 * ({ showTorchBanner: true, torchesLitCount: 0, loading: true })
 * on first render, then settles once the backend responds.
 *
 * Cached at module scope with a 60s revalidate window. `torchesLitCount`
 * reads from the backend's in-memory buffer (not the durable AppConfig
 * write) so callers see the live count without waiting for the 5s flush.
 */
export function useAppConfig(): AppConfigState {
  const [state, setState] = useState<AppConfigState>(() => ({
    showTorchBanner: cache?.showTorchBanner ?? DEFAULT_PAYLOAD.showTorchBanner,
    torchesLitCount: cache?.torchesLitCount ?? DEFAULT_PAYLOAD.torchesLitCount,
    peaceFlameRegisterTeamId:
      cache?.peaceFlameRegisterTeamId ??
      DEFAULT_PAYLOAD.peaceFlameRegisterTeamId,
    loading: cache == null,
  }));

  useEffect(() => {
    let mounted = true;

    const refresh = async () => {
      const fresh = await fetchAppConfig();
      if (mounted) {
        setState({
          showTorchBanner: fresh.showTorchBanner,
          torchesLitCount: fresh.torchesLitCount,
          peaceFlameRegisterTeamId: fresh.peaceFlameRegisterTeamId,
          loading: false,
        });
      }
    };

    if (cache == null || Date.now() - lastFetchAt > REVALIDATE_MS) {
      refresh();
    } else {
      setState({
        showTorchBanner: cache.showTorchBanner,
        torchesLitCount: cache.torchesLitCount,
        peaceFlameRegisterTeamId: cache.peaceFlameRegisterTeamId,
        loading: false,
      });
    }

    subscribers.add(refresh);
    return () => {
      mounted = false;
      subscribers.delete(refresh);
    };
  }, []);

  return state;
}
